import { DateTime } from '@ksyos/date-time';
import { Card, Col, Form, FormButtonRow, FormSection, Heading, IconButton, Row, Text } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ThirdPartySsoName } from '@common/enums/';
import type { IReduxState } from '@frontend/configureStore';
import { AuthorizationErrorCode, ManagerAuthorization } from '@common/enums/';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { EmptyListPlaceholder } from '../../elements/EmptyListPlaceholder';
import { CancelButton } from '../../userDataManagement/CancelButton';
import { SaveButton } from '../../userDataManagement/SaveButton';

interface IThirdPartySsoIdentifier {
    created_at: string | Date;
    id: string;
    name: ThirdPartySsoName;
}

export function SingleSignOnSettings() {
    const { id: practitionerId } = useParams() as { id: string };
    const abortableFetch = useAbortableFetch();
    const contextPractitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const isOwnUser = practitionerId === contextPractitionerId;
    const [ssoIdentifiers, setSsoIdentifiers] = useState<IThirdPartySsoIdentifier[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [ssoIdentifiersToDelete, setSsoIdentifiersToDelete] = useState<string[]>([]);

    const canEdit = hasOneAuthorization(ManagerAuthorization.practitionerAddEdit) || isOwnUser;

    if (!isOwnUser) {
        if (isManager) {
            assertHasAuthorization(ManagerAuthorization.practitionerRead);
        } else {
            throw new Error(AuthorizationErrorCode.CANNOT_VIEW_OTHER_PRACTITIONERS_PROFILE);
        }
    }

    useEffect(() => {
        loadThirdPartySsoIdentifiers();
    }, [practitionerId]);

    return (
        <>
            <Heading
                mb={8}
                size="xlarge"
            >
                {translateKey('practitionerSso.singleSignOn')}
            </Heading>
            {!ssoIdentifiers.length && getEmptyListPlaceholder()}
            {ssoIdentifiers.length > 0 && (
                <Card>
                    <Card.Transition type={isEditing ? 'edit' : 'read'} />
                    <Card.Header
                        action={
                            !isEditing && canEdit ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => setIsEditing(true)}
                                />
                            ) : undefined
                        }
                        title={translateKey('practitionerSso.links')}
                    />
                    <Card.Body>
                        <Form submitOnEnter={false}>
                            <FormSection>
                                <Row columnGap={2}>
                                    <Col span={5}>
                                        <Text>{translateKey('common.name')}</Text>
                                    </Col>
                                    <Col span={7}>
                                        <Text>{translateKey('common.activeSince')}</Text>
                                    </Col>
                                </Row>
                            </FormSection>
                            {getSsoIdentifiersFilteredOnToDelete().map((ssoIdentifier, index) => {
                                return (
                                    <FormSection key={ssoIdentifier.id}>
                                        <Row
                                            align="center"
                                            columnGap={2}
                                        >
                                            <Col span={5}>
                                                <Text fontWeight="medium">{ssoIdentifier.name}</Text>
                                            </Col>
                                            <Col span={5}>
                                                <Text fontWeight="medium">
                                                    {DateTime.from(ssoIdentifier.created_at).format('DD-MM-YYYY')}
                                                </Text>
                                            </Col>
                                            <Col span={2}>
                                                {isEditing && (
                                                    <IconButton
                                                        icon="delete"
                                                        onClick={() => handleDeleteRecord(ssoIdentifier.id)}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </FormSection>
                                );
                            })}
                            {isEditing && getCancelSaveButtonRow()}
                        </Form>
                    </Card.Body>
                </Card>
            )}
        </>
    );

    function getEmptyListPlaceholder() {
        return (
            <EmptyListPlaceholder
                subtitle={translateKey(`practitionerSso.${isManager ? 'noSsoAdmin' : 'noSsoPractitioner'}`)}
            />
        );
    }

    function getCancelSaveButtonRow() {
        return (
            <FormButtonRow>
                <CancelButton
                    disabled={false}
                    onCancel={async () => handleCancel()}
                />
                <SaveButton
                    disabled={false}
                    onSave={async () => handleSubmit()}
                />
            </FormButtonRow>
        );
    }

    function getSsoIdentifiersFilteredOnToDelete() {
        return ssoIdentifiers.filter((ssoIdentifier) => !ssoIdentifiersToDelete.includes(ssoIdentifier.id));
    }

    function handleCancel() {
        setSsoIdentifiersToDelete([]);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (ssoIdentifiersToDelete.length) {
            await deactivateThirdPartySsoIdentifiers();
            setSsoIdentifiers(getSsoIdentifiersFilteredOnToDelete());
        }
        setSsoIdentifiersToDelete([]);
        setIsEditing(false);
    }

    function handleDeleteRecord(ssoIdentifierId: string) {
        setSsoIdentifiersToDelete(uniq([...ssoIdentifiersToDelete, ssoIdentifierId]));
    }

    async function loadThirdPartySsoIdentifiers() {
        const url = `/api/practitioner/${practitionerId}/ssoIdentifiers`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setSsoIdentifiers(result.json.items);
    }

    async function deactivateThirdPartySsoIdentifiers() {
        const url = '/api/practitioner/ssoIdentifiers';
        await abortableFetch.fetchJson(url, 'POST', {
            thirdPartySsoIdentifierIds: ssoIdentifiersToDelete,
        });
    }
}
